import { useRef } from 'react';

import { JSONEditorError } from './components/JSONEditorError';
import { KeyValueInput } from './components/KeyValueInput';
import { PrettyJSONEditorContext, createJSONEditorStore } from './store';
import { JSONEditorProps } from './types';
import { useJSONEditorStore } from './useJSONEditorStore';

export function PrettyJSONEditorInternal() {
  const error = useJSONEditorStore((state) => state.error);
  const duplicateKeyError = useJSONEditorStore(
    (state) => state.duplicateKeyError
  );
  const localValues = useJSONEditorStore((state) => state.localValues);
  const keyOrder = useJSONEditorStore((state) => state.keyOrder);
  const pendingKey = useJSONEditorStore((state) => state.pendingKey);
  const showPendingInput = useJSONEditorStore(
    (state) => state.showPendingInput
  );
  const keyInputRefs = useJSONEditorStore((state) => state.keyInputRefs);
  const pendingKeyRef = useJSONEditorStore((state) => state.pendingKeyRef);
  const valueInputRefs = useJSONEditorStore((state) => state.valueInputRefs);
  const pendingValueInputRef = useJSONEditorStore(
    (state) => state.pendingValueInputRef
  );

  // Store actions
  const setPendingKey = useJSONEditorStore((state) => state.setPendingKey);

  // Show error if nested JSON is detected
  if (error) {
    return <JSONEditorError message={error} />;
  }

  return (
    <div className="flex w-full flex-col">
      {/* Existing key-value pairs */}
      {keyOrder.map((key, index) => (
        <KeyValueInput
          key={key}
          isNewInput={false}
          keyName={key}
          value={localValues[key]}
          valueInputRef={valueInputRefs[key]}
          isLast={index === keyOrder.length - 1}
          keyInputRef={keyInputRefs[key]}
          nextKeyInputRef={
            index < keyOrder.length - 1
              ? keyInputRefs[keyOrder[index + 1]]
              : undefined
          }
          index={index}
        />
      ))}

      {/* Pending input for new key-value pair */}
      {showPendingInput && pendingKeyRef && pendingValueInputRef && (
        <KeyValueInput
          isNewInput={true}
          keyInputRef={pendingKeyRef}
          valueInputRef={pendingValueInputRef}
          keyName={pendingKey}
          value=""
          setPendingKey={setPendingKey}
        />
      )}

      {duplicateKeyError && (
        <div className="rounded text-sm text-red-500">{duplicateKeyError}</div>
      )}
    </div>
  );
}

export const PrettyJSONEditor = ({
  value,
  setValue,
  readOnly = false,
  keyPlaceholder = 'Write key here...',
  valuePlaceholder = 'Write value here...',
}: JSONEditorProps) => {
  const pendingKeyRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const pendingValueInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(
    null
  );

  const store = useRef(
    createJSONEditorStore({
      value,
      setValue,
      readOnly,
      keyPlaceholder,
      valuePlaceholder,
      pendingKeyRef,
      pendingValueInputRef,
    })
  ).current;
  return (
    <PrettyJSONEditorContext.Provider value={store}>
      <PrettyJSONEditorInternal />
    </PrettyJSONEditorContext.Provider>
  );
};
